<template>
    <div>
        <NavbarOne/>

        <div class="flex items-center gap-4 flex-wrap bg-overlay p-14 sm:p-16 before:bg-title before:bg-opacity-70" :style="{backgroundImage:'url(' + bg + ')'}">
            <div class="text-center w-full">
                <h2 class="text-white text-8 md:text-[40px] font-normal leading-none text-center">Order Details</h2>
                <ul class="flex items-center justify-center gap-[10px] text-base md:text-lg leading-none font-normal text-white mt-3 md:mt-4 flex-wrap">
                    <li><router-link to="/index-v2">Home</router-link></li>
                    <li>/</li>
                    <li><router-link to="/order-history">Order History</router-link></li>
                    <li>/</li>
                    <li class="text-primary">{{ order?.number || 'Details' }}</li>
                </ul>
            </div>
        </div>

        <div class="s-py-100" data-aos="fade-up">
            <div class="container-fluid">
                <div class="max-w-[1720px] mx-auto flex items-start gap-8 md:gap-12 2xl:gap-24 flex-col md:flex-row my-profile-navtab">
                    <div class="w-full md:w-[200px] lg:w-[300px] flex-none">
                        <ProfileTab/>
                    </div>
                    <div class="w-full md:w-auto md:flex-1">
                        <div class="grid gap-6" v-if="order && !loading">
                            <section class="rounded-2xl border border-[#17243026] border-opacity-15 bg-[#F8F8F9] dark:bg-dark-secondary p-6 sm:p-8">
                                <div class="flex items-start justify-between gap-4 flex-col xl:flex-row">
                                    <div class="grid gap-3">
                                        <div class="flex items-center gap-3 flex-wrap">
                                            <h3 class="text-2xl md:text-3xl font-semibold leading-none text-title dark:text-white">{{ order.number }}</h3>
                                            <span :class="statusBadgeClass(order.status)" class="py-[7px] px-[10px] font-semibold leading-none text-white text-sm rounded inline-flex">
                                                {{ order.status_label || order.status }}
                                            </span>
                                        </div>
                                        <p class="text-base md:text-lg text-paragraph dark:text-white-light">Placed on {{ formatOrderDateTime(order.ordered_at) }}</p>
                                        <p class="text-sm md:text-base text-paragraph dark:text-white-light">Source: {{ labelize(order.source_channel) }}</p>
                                        <div v-if="order.delivery_counter_label" class="inline-flex w-fit rounded-full bg-[#BB976D]/10 px-4 py-2 text-sm font-semibold text-[#9A6E42]">
                                            {{ order.delivery_counter_label }}
                                        </div>
                                    </div>

                                    <div class="flex flex-col sm:flex-row gap-3">
                                        <a
                                            v-if="order.invoice_url"
                                            :href="order.invoice_url"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            class="btn btn-outline"
                                            data-text="Download Invoice"
                                        >
                                            <span>Download invoice</span>
                                        </a>
                                        <button
                                            v-if="order.can_cancel"
                                            type="button"
                                            class="btn btn-outline"
                                            data-text="Cancel Order"
                                            :disabled="cancelling"
                                            @click="cancelOrder"
                                        >
                                            <span>{{ cancelling ? 'Cancelling...' : 'Cancel order' }}</span>
                                        </button>
                                        <button
                                            type="button"
                                            class="btn btn-theme-solid"
                                            data-text="Reorder"
                                            :disabled="reordering"
                                            @click="reorder"
                                        >
                                            <span>{{ reordering ? 'Reordering...' : 'Reorder' }}</span>
                                        </button>
                                    </div>
                                </div>
                            </section>

                            <section class="grid lg:grid-cols-[1.2fr_0.8fr] gap-6">
                                <div class="rounded-2xl border border-[#17243026] border-opacity-15 bg-[#F8F8F9] dark:bg-dark-secondary p-6 sm:p-8">
                                    <div class="flex items-center justify-between gap-3 flex-wrap">
                                        <h4 class="text-xl font-semibold text-title dark:text-white">Items</h4>
                                        <div class="text-sm text-paragraph dark:text-white-light">{{ order.items.length }} item(s)</div>
                                    </div>
                                    <div class="mt-6 grid gap-5">
                                        <article
                                            v-for="item in order.items"
                                            :key="item.id"
                                            class="rounded-2xl border border-[#17243026] border-opacity-15 bg-white dark:bg-title/10 p-4 sm:p-5"
                                        >
                                            <div class="flex items-start justify-between gap-4 flex-col sm:flex-row">
                                                <div class="flex items-start gap-4">
                                                    <div class="w-20 sm:w-24 flex-none">
                                                        <img :src="item.image || fallbackImage" :alt="item.name">
                                                    </div>
                                                    <div>
                                                        <h5 class="text-lg font-semibold text-title dark:text-white">{{ item.name }}</h5>
                                                        <p v-if="item.description" class="mt-2 text-sm text-paragraph dark:text-white-light">{{ item.description }}</p>
                                                        <div v-if="item.selected_modifiers?.length" class="mt-3 grid gap-2">
                                                            <div
                                                                v-for="modifier in item.selected_modifiers"
                                                                :key="`${item.id}-${modifier.modifier_id || modifier.modifier_name}`"
                                                                class="text-sm text-paragraph dark:text-white-light"
                                                            >
                                                                <span class="font-medium text-title dark:text-white">{{ modifier.modifier_name }}:</span>
                                                                {{ formatModifierOptions(modifier.options) }}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="text-left sm:text-right">
                                                    <div class="text-sm text-paragraph dark:text-white-light">Qty {{ item.formatted_quantity }}</div>
                                                    <div class="mt-2 font-semibold text-title dark:text-white">{{ formatPrice(item.subtotal, order.currency) }}</div>
                                                </div>
                                            </div>
                                        </article>
                                    </div>
                                </div>

                                <div class="grid gap-6">
                                    <section class="rounded-2xl border border-[#17243026] border-opacity-15 bg-[#F8F8F9] dark:bg-dark-secondary p-6 sm:p-8">
                                        <h4 class="text-xl font-semibold text-title dark:text-white">Order Summary</h4>
                                        <div class="mt-5 grid gap-3 text-sm md:text-base">
                                            <div class="flex justify-between gap-4"><span class="text-paragraph dark:text-white-light">Payment</span><span class="font-semibold text-title dark:text-white">{{ order.payment_status_label }}</span></div>
                                            <div class="flex justify-between gap-4"><span class="text-paragraph dark:text-white-light">Delivery</span><span class="font-semibold text-title dark:text-white">{{ order.delivery_status_label }}</span></div>
                                            <div v-if="order.estimated_delivery_days" class="flex justify-between gap-4"><span class="text-paragraph dark:text-white-light">Delivery estimate</span><span class="font-semibold text-title dark:text-white">{{ order.estimated_delivery_days }} days</span></div>
                                            <div v-if="order.estimated_delivery_date" class="flex justify-between gap-4"><span class="text-paragraph dark:text-white-light">Expected by</span><span class="font-semibold text-title dark:text-white">{{ formatOrderDateTime(order.estimated_delivery_date) }}</span></div>
                                            <div class="flex justify-between gap-4"><span class="text-paragraph dark:text-white-light">Subtotal</span><span class="font-semibold text-title dark:text-white">{{ formatPrice(order.subtotal, order.currency) }}</span></div>
                                            <div class="flex justify-between gap-4"><span class="text-paragraph dark:text-white-light">Discount</span><span class="font-semibold text-title dark:text-white">{{ formatPrice(order.discount_amount, order.currency) }}</span></div>
                                            <div class="flex justify-between gap-4"><span class="text-paragraph dark:text-white-light">Tax</span><span class="font-semibold text-title dark:text-white">{{ formatPrice(order.tax_amount, order.currency) }}</span></div>
                                            <div class="flex justify-between gap-4"><span class="text-paragraph dark:text-white-light">Delivery fee</span><span class="font-semibold text-title dark:text-white">{{ formatPrice(order.delivery_amount, order.currency) }}</span></div>
                                            <div class="flex justify-between gap-4 border-t border-bdr-clr dark:border-bdr-clr-drk pt-3"><span class="font-semibold text-title dark:text-white">Total</span><span class="font-semibold text-title dark:text-white">{{ formatPrice(order.total, order.currency) }}</span></div>
                                        </div>
                                    </section>

                                    <section class="rounded-2xl border border-[#17243026] border-opacity-15 bg-[#F8F8F9] dark:bg-dark-secondary p-6 sm:p-8">
                                        <h4 class="text-xl font-semibold text-title dark:text-white">Contact & Delivery</h4>
                                        <div class="mt-5 grid gap-4 text-sm md:text-base">
                                            <div>
                                                <div class="text-paragraph dark:text-white-light">Name</div>
                                                <div class="mt-1 font-semibold text-title dark:text-white">{{ order.contact_name || '—' }}</div>
                                            </div>
                                            <div>
                                                <div class="text-paragraph dark:text-white-light">Phone</div>
                                                <div class="mt-1 font-semibold text-title dark:text-white">{{ order.contact_phone || '—' }}</div>
                                            </div>
                                            <div>
                                                <div class="text-paragraph dark:text-white-light">Email</div>
                                                <div class="mt-1 font-semibold text-title dark:text-white break-all">{{ order.contact_email || '—' }}</div>
                                            </div>
                                            <div>
                                                <div class="text-paragraph dark:text-white-light">Location</div>
                                                <div class="mt-1 font-semibold text-title dark:text-white">{{ formatShippingAddress(order) }}</div>
                                            </div>
                                            <div v-if="order.customer_note">
                                                <div class="text-paragraph dark:text-white-light">Customer note</div>
                                                <div class="mt-1 text-title dark:text-white">{{ order.customer_note }}</div>
                                            </div>
                                        </div>
                                    </section>
                                </div>
                            </section>

                            <section class="rounded-2xl border border-[#17243026] border-opacity-15 bg-[#F8F8F9] dark:bg-dark-secondary p-6 sm:p-8">
                                <h4 class="text-xl font-semibold text-title dark:text-white">Tracking Timeline</h4>
                                <div v-if="order.timeline?.length" class="mt-6 grid gap-4">
                                    <article
                                        v-for="entry in order.timeline"
                                        :key="entry.id"
                                        class="rounded-2xl border border-[#17243026] border-opacity-15 bg-white dark:bg-title/10 p-5"
                                    >
                                        <div class="flex items-center justify-between gap-3 flex-wrap">
                                            <h5 class="text-lg font-semibold text-title dark:text-white">{{ entry.title }}</h5>
                                            <div class="text-sm text-paragraph dark:text-white-light">{{ formatOrderDateTime(entry.tracked_at) }}</div>
                                        </div>
                                        <p class="mt-2 text-sm md:text-base text-paragraph dark:text-white-light">
                                            {{ entry.description || 'No extra notes for this update.' }}
                                        </p>
                                    </article>
                                </div>
                                <p v-else class="mt-4 text-sm md:text-base text-paragraph dark:text-white-light">No tracking updates yet. We will add them as your order progresses.</p>
                            </section>
                        </div>

                        <div v-if="loading" class="rounded-2xl border border-[#17243026] border-opacity-15 bg-[#F8F8F9] dark:bg-dark-secondary p-6 sm:p-8 text-base md:text-lg">
                            Loading your order details...
                        </div>
                        <div v-if="errorMessage" class="rounded-2xl border border-[#E13939]/20 bg-[#FFF5F5] p-6 text-sm text-[#E13939]">
                            {{ errorMessage }}
                        </div>
                        <p v-if="successMessage" class="mt-4 text-sm text-[#31A051]">{{ successMessage }}</p>
                    </div>
                </div>
            </div>
        </div>

        <FooterOne/>

        <ScrollToTop/>
    </div>
</template>

<script setup>
    import { onMounted, ref } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import NavbarOne from '@/components/navbar/navbar-one.vue';
    import ProfileTab from '@/components/profile-tab.vue';
    import FooterOne from '@/components/footer/footer-one.vue';
    import ScrollToTop from '@/components/scroll-to-top.vue';
    import bg from '@/assets/img/shortcode/breadcumb.jpg'
    import { PRODUCT_PLACEHOLDER as fallbackImage } from '@/lib/placeholder'
    import Aos from 'aos';
    import { apiRequest } from '@/lib/client-api';
    import { useClientSession } from '@/composables/use-client-session';

    const route = useRoute();
    const router = useRouter();
    const session = useClientSession();
    const order = ref(null);
    const loading = ref(true);
    const reordering = ref(false);
    const cancelling = ref(false);
    const errorMessage = ref('');
    const successMessage = ref('');

    function statusBadgeClass(status) {
        switch (status) {
            case 'delivered':
                return 'bg-[#31A051]';
            case 'processing':
                return 'bg-[#2563EB]';
            case 'confirmed':
                return 'bg-[#7C3AED]';
            case 'ready':
                return 'bg-[#0F766E]';
            case 'cancelled':
                return 'bg-[#E13939]';
            default:
                return 'bg-[#EC991D]';
        }
    }

    function formatPrice(value, currency = 'USD') {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency,
            minimumFractionDigits: 2,
        }).format(Number(value || 0));
    }

    function formatOrderDateTime(value) {
        if (!value) {
            return 'Date unavailable';
        }

        return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        }).format(new Date(value));
    }

    function formatShippingAddress(currentOrder) {
        return [
            currentOrder.shipping_address,
            currentOrder.shipping_city,
            currentOrder.shipping_country,
        ].filter(Boolean).join(', ') || 'Shipping address unavailable';
    }

    function formatModifierOptions(options = []) {
        return options
            .map((option) => option.label)
            .filter(Boolean)
            .join(', ') || 'Custom selection';
    }

    function labelize(value) {
        return value ? String(value).replace(/_/g, ' ') : '—';
    }

    async function loadOrder() {
        loading.value = true;
        errorMessage.value = '';

        try {
            const response = await apiRequest(`/orders/${route.params.id}`, {
                token: session.token.value,
            });

            order.value = response.data;
        } catch (error) {
            errorMessage.value = error.message || 'Unable to load this order right now.';
        } finally {
            loading.value = false;
        }
    }

    async function reorder() {
        if (!order.value) {
            return;
        }

        reordering.value = true;
        errorMessage.value = '';
        successMessage.value = '';

        try {
            const response = await apiRequest(`/orders/${order.value.id}/reorder`, {
                method: 'POST',
                token: session.token.value,
            });

            successMessage.value = `Order ${response.data?.number || ''} has been placed successfully.`;

            router.push({
                path: '/thank-you',
                query: {
                    order: response.data?.number || '',
                    invoice: response.data?.invoice_url || '',
                    customerMail: response.meta?.customer_mail_sent ? '1' : '0',
                    adminMail: response.meta?.admin_mail_sent ? '1' : '0',
                },
            });
        } catch (error) {
            errorMessage.value = error.message || 'Unable to reorder this order right now.';
        } finally {
            reordering.value = false;
        }
    }

    async function cancelOrder() {
        if (!order.value || !window.confirm(`Cancel order ${order.value.number}?`)) {
            return;
        }

        cancelling.value = true;
        errorMessage.value = '';
        successMessage.value = '';

        try {
            const response = await apiRequest(`/orders/${order.value.id}/cancel`, {
                method: 'POST',
                token: session.token.value,
            });

            order.value = response.data;
            successMessage.value = response.message || `Order ${order.value.number} was cancelled successfully.`;
        } catch (error) {
            errorMessage.value = error.message || 'Unable to cancel this order right now.';
        } finally {
            cancelling.value = false;
        }
    }

    onMounted(async () => {
        Aos.init();
        await session.initializeSession();
        await loadOrder();
    });
</script>
